import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { callScrapeAndSummarise, createPendingRecipe } from '@recipe-rack/shared';

import { db, functions } from '../Recipe/Firebase/InitialiseFirebase';
import { useToast } from '../../design/components';

export interface PasteBarProps {
  userId: string;
  /**
   * Called immediately after the pending recipe document has been written.
   * The parent can use this to flash an optimistic "scraping…" state until
   * the snapshot stream picks the recipe up.
   */
  onSubmitted?: (recipeId: string) => void;
}

/**
 * URL input + submit. On submit:
 *  1. Generate a recipeId via uuid.
 *  2. Write a pending placeholder doc (`version: 0`).
 *  3. Call the `scrape_and_summarise` callable.
 *  4. Surface errors via toast — the snapshot stream is the source of truth.
 */
const PasteBar: React.FC<PasteBarProps> = ({ userId, onSubmitted }) => {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const link = value.trim();
    if (!link || busy) return;
    const recipeId = uuidv4();
    setBusy(true);
    try {
      await createPendingRecipe(db, userId, {
        recipeId,
        title: '',
        ingredients: [],
        method: [],
        link,
        version: 0,
        rackIds: [],
      });
      setValue('');
      onSubmitted?.(recipeId);
      // Fire and forget — the snapshot stream will surface the final doc.
      try {
        await callScrapeAndSummarise(functions, { link, recipeId, userId });
      } catch (innerErr) {
        // Scrape failure: tell the user but keep the pending row so they can retry/delete it later.
        toast.show("We couldn't read that page. Try a different link?", 'error');
      }
    } catch (err) {
      toast.show('Something went wrong saving that link.', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="rr-pastebar" onSubmit={submit}>
      <input
        type="url"
        placeholder="Paste a URL…"
        aria-label="Recipe URL"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        disabled={busy}
      />
      <button type="submit" disabled={busy || !value.trim()}>
        {busy ? <span className="rr-pastebar-loader" aria-hidden="true" /> : 'Add'}
      </button>
    </form>
  );
};

export default PasteBar;
